import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SfInvoiceOpportunity } from '@/types';

interface InvoiceHistoryTableProps {
  invoices: SfInvoiceOpportunity[];
}

function formatDate(dateStr: string): string {
  // Extract just the date portion (YYYY-MM-DD) to avoid timezone conversion issues
  const datePart = dateStr.split('T')[0];
  // Parse as local time by appending T12:00:00 (noon) to avoid any edge cases
  return new Date(datePart + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCurrency(amount: number | null): string {
  if (amount === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function InvoiceHistoryTable({ invoices }: InvoiceHistoryTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (invoices.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Invoice History</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">No invoice history</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Invoice History (Last 12 Months)</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-y">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoices.map((invoice) => {
                const isExpanded = expandedId === invoice.id;
                const hasLineItems = invoice.line_items && invoice.line_items.length > 0;

                return (
                  <>
                    <tr
                      key={invoice.id}
                      className={`${hasLineItems ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                      onClick={() => hasLineItems && setExpandedId(isExpanded ? null : invoice.id)}
                    >
                      <td className="px-4 py-3">
                        {hasLineItems && (
                          <svg
                            className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                          </svg>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">{formatDate(invoice.close_date)}</td>
                      <td className="px-4 py-3 text-sm">{invoice.type}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{formatCurrency(invoice.amount)}</td>
                    </tr>
                    {isExpanded && hasLineItems && (
                      <tr key={`${invoice.id}-items`}>
                        <td colSpan={4} className="px-4 py-2 bg-gray-50">
                          <div className="pl-8">
                            <div className="text-xs font-medium text-gray-500 mb-2">Line Items</div>
                            <div className="space-y-1">
                              {invoice.line_items!.map((item) => (
                                <div key={item.id} className="flex justify-between text-sm">
                                  <span className="text-gray-600">{item.product_name}</span>
                                  <span className="font-medium">{formatCurrency(item.amount)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
